import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = '1os_access_token';

const api = axios.create({
  baseURL: `${API}/api`,
  // Bearer-only auth. Do NOT enable withCredentials — it attaches browser
  // cookies (e.g. Cloudflare `__cf_bm`) to every API call, which can push
  // request headers past proxy limits and trigger 520s. The Authorization
  // header added by the interceptor below is the single source of identity.
  withCredentials: false,
  timeout: 120000, // 2 minutes — confirm/validate can take time on large files
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor: attach Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401/403
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        // Token expired or invalid — clear and redirect to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('1os_user_info');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      if (status === 403) {
        const detail = data?.detail;
        // Check for MODULE_DISABLED
        if (detail && typeof detail === 'object' && detail.code === 'MODULE_DISABLED') {
          console.error('Module disabled:', detail.message);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me')
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getBusinessPulse: (params) => api.get('/dashboard/business-pulse', { params }),
  getDiscounts: (params) => api.get('/dashboard/discounts', { params }),
  getSalesByDay: (days = 7) => api.get(`/dashboard/sales-by-day?days=${days}`),
  getTrustSummary: (businessDate) => api.get('/dashboard/trust-summary', { params: { business_date: businessDate } }),
  getWaiverSummary: (businessDate, limit = 25) => api.get('/dashboard/waiver-summary', { params: { business_date: businessDate, limit } }),
};

export const serviceChargeApi = {
  getSummary: (params = {}) => api.get('/service-charge/summary', { params }),
};

// Table Efficiency — Phase 1 (Operations module). No KOT timestamps yet,
// so the response carries `meta.limitations` with explicit data-honesty copy.
export const tableEfficiencyApi = {
  get: (params = {}) => api.get('/operations/table-efficiency', { params }),
  getServers: (params = {}) => api.get('/operations/server-efficiency', { params }),
};

// Order timeline (Phase 6 — /sale drill-down). Returns the 5-event KOT
// timeline + Phase 2 delays block. When KOT data is missing, prep/dining
// durations return null and `meta.kot_data_confidence === 'low'` — the
// UI MUST render "—" not "0 min".
export const orderTimelineApi = {
  get: (orderId) => api.get(`/sales/order/${orderId}/timeline`),
};

// Outlet operational settings (Phase 4 — timezone + cutoff_hour).
// cutoff_hour is stored but query-side rollover is deferred; the
// response carries `cutoff_hour_behavior_active=false` so the UI can
// display a small caveat on the selector.
export const outletSettingsApi = {
  get: (outletId) => api.get(`/outlets/${outletId}/settings`),
  put: (outletId, body) => api.put(`/outlets/${outletId}/settings`, body),
};

// Sales Sources
export const sourcesApi = {
  list: (status) => api.get('/sales-sources', { params: { status } }),
  get: (id) => api.get(`/sales-sources/${id}`),
  create: (data) => api.post('/sales-sources', data),
  addCredentials: (id, data) => api.post(`/sales-sources/${id}/credentials`, data),
  sync: (id, mode = 'incremental') => api.post(`/sales-sources/${id}/sync`, null, { params: { mode } }),
  backfill: (id, dateFrom, dateTo) => api.post(`/sales-sources/${id}/backfill`, null, { params: { date_from: dateFrom, date_to: dateTo } }),
  backfillReplay: (id, data) => api.post(`/sales-sources/${id}/backfill-replay`, data),
  getSyncRuns: (id, limit = 20) => api.get(`/sales-sources/${id}/sync-runs`, { params: { limit } }),
  getHealthTimeline: (id, days = 30) => api.get(`/sales-sources/${id}/health-timeline`, { params: { days } }),
};

// Integration Documents
export const integrationsApi = {
  uploadDocument: (file, salesSourceId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (salesSourceId) formData.append('sales_source_id', salesSourceId);
    return api.post('/integrations/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  analyzeDocument: (docId) => api.post(`/integrations/documents/${docId}/analyze`),
  getBlueprint: (id) => api.get(`/integrations/blueprints/${id}`),
  getPosHealth: () => api.get('/integrations/pos/health')
};

// Uploads
export const uploadsApi = {
  uploadSalesFile: (file, uploadType, outletId, salesSourceId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', uploadType);
    if (outletId) formData.append('outlet_id', outletId);
    if (salesSourceId) formData.append('sales_source_id', salesSourceId);
    return api.post('/uploads/sales', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (status) => api.get('/uploads/sales', { params: { status } }),
  getDetail: (uploadId) => api.get(`/uploads/sales/${uploadId}/detail`),
  mapColumns: (fileId, data) => api.post(`/uploads/sales/${fileId}/map-columns`, data),
  validate: (fileId) => api.post(`/uploads/sales/${fileId}/validate`),
  confirm: (uploadId) => api.post(`/uploads/sales/${uploadId}/confirm`),
  revalidate: (uploadId) => api.post(`/uploads/sales/${uploadId}/revalidate`),
  replace: (uploadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/uploads/sales/${uploadId}/replace`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (uploadId, body) => api.delete(`/uploads/sales/${uploadId}`, { data: body }),
  deleteImpact: (uploadId) => api.get(`/uploads/${uploadId}/delete-impact`),
  canonicalPreview: (fileId, limit) => api.get(`/uploads/sales/${fileId}/canonical-preview`, { params: { limit } }),
};

// Sales Data
export const salesApi = {
  getDailySummary: (outletId, date) => api.get('/sales/daily-summary', { params: { outlet_id: outletId, date } }),
  getItemConsumption: (outletId, date) => api.get('/sales/item-consumption', { params: { outlet_id: outletId, date } }),
  getSyncOrders: (outletId, sinceCursor, limit = 100) => api.get('/sales-sync/orders', { params: { outlet_id: outletId, since_cursor: sinceCursor, limit } }),
  listOrders: (params) => api.get('/sales/orders', { params }),
  getOrder: (id) => api.get(`/sales/orders/${id}`),
  getOrderExplainability: (id) => api.get(`/sales/orders/${id}/explainability`),
  getHourlyAnalytics: (params) => api.get('/sales/analytics/hourly', { params }),
  getChannelAnalytics: (params) => api.get('/sales/analytics/channels', { params }),
  getTopItems: (params) => api.get('/sales/items/top', { params }),
  getItemTrends: (item, params) => api.get('/sales/items/trends', { params: { item, ...params } }),
  getCategoryAnalytics: (params) => api.get('/sales/items/categories', { params }),
  getMovers: (params) => api.get('/sales/items/movers', { params }),
  getItemFilters: (params) => api.get('/sales/items/filters', { params }),
  getDailyItemTrend: (params) => api.get('/sales/items/daily-trend', { params }),
  getWeekdayAnalytics: (params) => api.get('/sales/items/weekday', { params }),
  getItemsByHour: (params) => api.get('/sales/items-by-hour', { params }),
  getTimeIntelligence: (params) => api.get('/sales/time-intelligence', { params }),
  getWeekdayTopItems: (params) => api.get('/sales/items/day-of-week/top-items', { params }),
  getTransactionContext: (params) => api.get('/sales/items/transaction-context', { params }),
  getItemsByPartner: (params) => api.get('/sales/items/by-partner', { params }),
  getLineage: (entityType, entityId) => api.get('/sales/source-lineage', { params: { entity_type: entityType, entity_id: entityId } }),
  getReconciliation: (params) => api.get('/sales/reconciliation', { params }),
  acknowledgeIssue: (id) => api.post(`/sales/reconciliation/${id}/acknowledge`),
  resolveIssue: (id) => api.post(`/sales/reconciliation/${id}/resolve`)
};

// Sync Schedules
export const schedulerApi = {
  list: () => api.get('/sync-schedules'),
  get: (sourceId) => api.get(`/sync-schedules/${sourceId}`),
  upsert: (sourceId, data) => api.post(`/sync-schedules/${sourceId}`, data),
};

// Mappings
// Removed in Feb 2026 — Sales Capture is a canonical ingestion engine and no
// longer owns semantic mapping. Item meaning lives in Inventory's recipe layer.
// If you need to re-enable dormant mapping endpoints, restore the router group
// in backend/server.py and re-add the api bindings here.

// Item Uploads
export const itemUploadsApi = {
  upload: (file, outletId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', 'ITEM_LEVEL');
    if (outletId) formData.append('outlet_id', outletId);
    return api.post('/uploads/items', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  list: (status) => api.get('/uploads/items', { params: { status } }),
  getDetail: (uploadId) => api.get(`/uploads/items/${uploadId}/detail`),
  mapColumns: (uploadId, data) => api.post(`/uploads/items/${uploadId}/map-columns`, data),
  validate: (uploadId) => api.post(`/uploads/items/${uploadId}/validate`),
  confirm: (uploadId) => api.post(`/uploads/items/${uploadId}/confirm`),
  revalidate: (uploadId) => api.post(`/uploads/items/${uploadId}/revalidate`),
  delete: (uploadId, body) => api.delete(`/uploads/items/${uploadId}`, { data: body }),
  deleteImpact: (uploadId) => api.get(`/uploads/${uploadId}/delete-impact`),
  canonicalPreview: (uploadId, limit) => api.get(`/uploads/items/${uploadId}/canonical-preview`, { params: { limit } }),
  reconciliation: (businessDate) => api.get('/uploads/items/reconciliation', { params: { business_date: businessDate } }),
};

// Outlets
export const outletsApi = {
  list: () => api.get('/outlets')
};

// Partners
export const partnersApi = {
  list: (status) => api.get('/partners', { params: { status } }),
  get: (id) => api.get(`/partners/${id}`),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.put(`/partners/${id}`, data),
  delete: (id, body) => api.delete(`/partners/${id}`, { data: body }),
  getUsage: (id) => api.get(`/partners/${id}/usage`),
  listMappings: (partnerId) => api.get('/partner-mappings', { params: { partner_id: partnerId } }),
  createMapping: (data) => api.post('/partner-mappings', data),
  getSummary: (params) => api.get('/sales/partners/summary', { params }),
  getTrends: (params) => api.get('/sales/partners/trends', { params }),
};

export default api;
